import { withAuthenticationRequired } from '@auth0/auth0-react'
import { Layout } from 'components/layout'
import { Route as Paths } from 'constants/'
import { useAccessToken, useFeatureFlags, useIsAppointmentGuidesEnabled, useTrackPageView, useUser } from 'hooks'
import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { AppointmentGuides } from '../../../AppointmentGuides'
import { Customers } from '../../../Customers'
import { CustomersGlobalSearch } from '../../../CustomersGlobalSearch'
import { Director } from '../../../Director'
import { Forms } from '../../../Forms'
import { Home } from '../../../Home'
import { NotFound } from '../../../NotFound'
import { Notifications } from '../../../Notifications'
import { Settings } from '../../../Settings'
import { InventoryManagementSystemClient } from 'clients/InventoryManagementSystemClient'

const IDENTIFY_POLL_INTERVAL_MS = 50
const IDENTIFY_TIMEOUT_MS = 3000

// Flags are only accurate for this user once Identify's identify() call has landed. The SDK swaps in the new context
// after it has fetched that context's flags, so a matching key means the flags are ready. Gives up after a timeout so a
// LaunchDarkly outage can't block the portal.
const waitForIdentify = (client: LDClient, contextKey: string) =>
  new Promise<void>((resolve) => {
    const startedAt = Date.now()

    const interval = setInterval(() => {
      const isIdentified = client.getContext()?.key === contextKey
      const hasTimedOut = Date.now() - startedAt > IDENTIFY_TIMEOUT_MS

      if (!isIdentified && !hasTimedOut) return

      clearInterval(interval)
      resolve()
    }, IDENTIFY_POLL_INTERVAL_MS)
  })

const RouteContainer = () => {
  const { isLoading } = useAccessToken()
  const client = useLDClient()
  const { accountId } = useUser()
  const { pathname } = useLocation()
  // Mirrors the key Providers/FeatureFlag/Identify sends to LaunchDarkly
  const identifiedContextKey = accountId || 'anonymous'

  const [isSettingsPageEnabled, isNotificationsFeedHidden] = useFeatureFlags([
    'SETTINGS_PAGES',
    'HIDE_NOTIFICATIONS_FEED',
  ])
  const isAppointmentGuidesEnabled = useIsAppointmentGuidesEnabled()
  const [areFeatureFlagsLoaded, beFeatureFlagsLoaded] = useState(false)
  const isAppReady = Boolean(areFeatureFlagsLoaded && !isLoading)

  useEffect(() => {
    if (areFeatureFlagsLoaded || !client) {
      return
    }

    let isCancelled = false
    let timeout: ReturnType<typeof setTimeout> | undefined

    const async = async () => {
      await client.waitForInitialization()
      await waitForIdentify(client, identifiedContextKey)

      if (isCancelled) return

      // HACK: There is lag time between the LD client saying it's ready and the flag value being accurate
      timeout = setTimeout(() => beFeatureFlagsLoaded(true), 100)
    }

    async()

    return () => {
      isCancelled = true
      clearTimeout(timeout)
    }
  }, [client, identifiedContextKey])

  // Skip the page view for a redirected /notifications visit so it doesn't count as feed usage
  const isRedirectedNotificationsVisit = isNotificationsFeedHidden && pathname.startsWith(Paths.NOTIFICATIONS)

  useTrackPageView(isAppReady && !isRedirectedNotificationsVisit)

  if (!isAppReady) {
    // Block the auth routes from rendering until our dependencies are loaded
    return null
  }

  const rootLoader = document.getElementById('root_loader')

  if (rootLoader) rootLoader.style.display = 'none'

  const SettingsOrRedirect = () => (isSettingsPageEnabled ? <Settings /> : <Navigate replace to={Paths.HOME} />)

  return (
    <Routes>
      <Route element={<Layout />} path="/">
        <Route element={<Home />} index />
        <Route element={<CustomersGlobalSearch />} path={`${Paths.GLOBAL_CUSTOMERS}/*`} />
        <Route element={<Customers />} path={`${Paths.CUSTOMERS}/*`} />
        <Route element={<InventoryManagementSystemClient />} path={`${Paths.INVENTORY_MANAGEMENT_SYSTEM}/*`} />
        <Route element={<Home />} path={`${Paths.HOME}/*`} />
        <Route
          element={isNotificationsFeedHidden ? <Navigate replace to={Paths.HOME} /> : <Notifications />}
          path={`${Paths.NOTIFICATIONS}/*`}
        />
        <Route
          element={isAppointmentGuidesEnabled ? <AppointmentGuides /> : <Navigate replace to={Paths.HOME} />}
          path={`${Paths.APPOINTMENT_GUIDES}/*`}
        />
        <Route element={<Forms />} path={`${Paths.FORMS}/*`} />
        <Route element={<SettingsOrRedirect />} path={`${Paths.SETTINGS}/*`} />
        <Route element={<NotFound />} path="/*" />
      </Route>
      <Route element={<Director />} path={Paths.REDIRECT} />
    </Routes>
  )
}

const AuthenticatedRoutes = withAuthenticationRequired(RouteContainer)

export { AuthenticatedRoutes }
